/**
 * WebOrder.tsx
 * @description :: This page is the Web Order tab for web orders made through the Web Application.
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Center,
  Select,
  Pagination,
  Checkbox,
  Tabs,
  Flex,
  Button,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconEye,
  IconCalendar,
  IconTableExport,
} from "@tabler/icons-react";
import * as XLSX from 'xlsx';
import classes from "./WebOrder.module.css";
// import DeleteStaff from "./DeleteStaff";
import { RowDataWebOrder } from "./types";
import { props } from "./props";
import { CustomTable } from "../Table/CustomTable";
import { webOrder } from "../types/RowDataTypes";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  showErrorAlert,
  showEditWarning,
  showSuccessAlert,
} from "../../helper/alerts";
import Header from "../static/Header";
import getWebOrderTable from "../../DataDisplay/DataDisplayTable/WebOrderTable";
import ScoreCard from "./ScoreCard";

enum ColumnHeaderToFilterPair {
    ORDERID = "Order ID",
    FULLNAME = "Customer Name",
    PHONENUMBER = "Phone Number",
    PRICE = "Amount (RM)",
    PAIDAT = "Order Date",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.ORDERID]: "salesOrderId",
  [colFilter.FULLNAME]: "fullname",
  [colFilter.PHONENUMBER]: "phone_number",
  [colFilter.PRICE]: "price",
  [colFilter.PAIDAT]: "order_date"
};

function Th({ children, sortable, reversed, sorted, onSort }: props) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  let allowedSorts;

  allowedSorts = [
    colFilter.PRICE.toString().toLowerCase(),
    // colFilter.FULLNAME.toString().toLowerCase(),
    // colFilter.CREATEDAT.toString().toLowerCase(),
    // colFilter.CONTACTNO.toString().toLowerCase(),
    // colFilter.EMAIL.toString().toLowerCase(),
    // colFilter.DOB.toString().toLowerCase(),
    colFilter.PAIDAT.toString().toLowerCase(),
];
  const isCurrent =
    window.location.href.split("/")[5] === "pending" ? true : false;
//   console.log("the children");
//   console.log(children);
//   console.log(allowedSorts);
//   console.log(allowedSorts.includes(children!!.toString().toLowerCase()));

  return children!! === "action" && !isCurrent ? (
    <></>
  ) : (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <div className={classes.sortableHeader}>
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </div>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataWebOrder[] = [
  {
    id: 0,
    uid: "",
    fullname: "",
    phone_number: "",

    salesOrderId: "",

    price: 0,

    courierService: "",
    trackingNumber: "",

    status: 0,

    paidAt: "",
    createdAt: "",
    updatedAt: "",

    action: "",
  },
];

export function WebOrder() {
  const axiosPrivate = useAxiosPrivate();
  const nav = useNavigate();
  const [search, setSearch] = useState<string>("");
  // const [sortedData, setSortedData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataWebOrder | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const [tableStateChanged, setTableStateChanged] = useState(0);
  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const resetPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  };

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.ORDERID],
    colFilters[colFilter.FULLNAME],
    colFilters[colFilter.PHONENUMBER]
  ];

  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const location = useLocation();
  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "paidAt",
    reverseSortDirection ? "ASC" : "DESC",
    webOrder,
    getWebOrderTable().create(),
    {
      current: location.pathname.split("/")[2] ?? "pending",
    //   current: "unverified",
      startDate: dateRange[0],
      endDate: dateRange[1]
    },
  );

  const itemsOnPage = useRef(0);
  const totalRows = fetchedData.totalRows;
  const rowData: RowDataWebOrder[] | null = fetchedData.data as RowDataWebOrder[];
  const isLoading: boolean = fetchedData.isLoading;
  const [RowDataDealer, setRowDataDealer] = useState<RowDataWebOrder[] | null>(null);

  const [pendingCount, setPendingCount] = useState(0);
  const [packingCount, setPackingCount] = useState(0);
  const [fulfilledCount, setFulfilledCount] = useState(0);
  const [currMthCount, setCurrMthCount] = useState(0);
  const [prevMthCount, setPrevMthCount] = useState(0);

  const currentDateMY = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" })
  );

  const setWebOrderScoreCard = () => {
    axiosPrivate
      .post("/api/admin/orders/get-weborderpage-stats", {
        currentDate: currentDateMY.toISOString().slice(0, 10),
      })
      .then((response) => {
        // console.log(response.data)
        setPendingCount(response.data.pendingCount);
        setPackingCount(response.data.packingCount);
        setFulfilledCount(response.data.fulfilledCount);
        setCurrMthCount(response.data.currentMonthEarnings);
        setPrevMthCount(response.data.prevMonthEarnings);
      })
      .catch((err) => {
        showErrorAlert(err)
      });
  }

  useEffect(() => {
    setRowDataDealer(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setWebOrderScoreCard();
  }, [rowData]);

  useEffect(() => {
    if ((dateRange[0] !== null) && (dateRange[1] === null)) return;  // Ensure start AND end date is specified.
    changeTableState();
  }, [dateRange])

  const [selectedRow, setSelectedRow] = useState<RowDataWebOrder | null>(null);
  const [isEditMode, setEditMode] = useState(false);
  const handleEdit = (row: RowDataWebOrder) => {
    setSelectedRow(row);

    // Check if the current page is "pass", then show the edit warning
    if (location.pathname.split("/")[2] === "pending") {
      showEditWarning("Are you sure you want to edit this dealer?", setEditMode);
    } else {
      // If the page is not "pass", directly setEditMode to true
      setEditMode(true);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // const handleVerifyDealer = (
  //   editedData: RowDataWebOrder,
  //   originalData: RowDataWebOrder | null,
  //   action: string,
  //   remark: string,
  // ) => {
  //   if (originalData) {
  //     const paramsToUpdate = {
  //       // uid: originalData.uid,
  //       // action: action,
  //       // remark: remark,
  //     };

  //     axiosPrivate
  //       .post("/api/admin/dealer/verify-dealer", paramsToUpdate)
  //       .then((response) => {
  //         if (
  //           itemsOnPage.current == 1 &&
  //           currentPage !== 1 &&
  //           currentPage == Math.ceil(totalRows / itemsPerPage)
  //         )
  //           setCurrentPage((prev) => prev - 1);
  //         else changeTableState();
  //         showSuccessAlert("Dealer Successfully Updated");
  //       })
  //       .catch((error) => {
  //         showErrorAlert("An error has occured");
  //       });
  //   }

  //   setEditMode(false);
  // };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  const isCurrent =
    location.pathname.split("/")[2] === "pending" ? true : false;
  const rowsToMap = RowDataDealer ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td >{row.salesOrderId}</Table.Td>
      <Table.Td>{row.fullname}</Table.Td>
      <Table.Td>{row.phone_number}</Table.Td>
      <Table.Td>{row.price}</Table.Td>
      <Table.Td>{row.paidAt ?
        new Intl.DateTimeFormat("en-MY", {
          timeZone: "Asia/Kuala_Lumpur",
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true
        }).format(new Date(row.paidAt)) : "Invalid Transaction"     
      }</Table.Td>
      <Table.Td className={classes.actionButtons}>
        <UnstyledButton component={Link} to={`/web-order/view/${row?.salesOrderId}`}>
            <button className={classes.editbutton}>
                <IconEye /> View Order
            </button>
        </UnstyledButton>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  

  const handleTabChange = (val: string | null) => {
    val = val ?? "pending";
    nav(`/web-order/${val}`);
    changeTableState();
  };

  const handleSorting = (val: any) => {
    setReverseSortDirection(!reverseSortDirection);
    setSortBy(val);
    changeTableState();
    // console.log("sorting");
    // console.log(val);
    // console.log(reverseSortDirection);
  };

  const handleExportTable = async () => {
    try {
      const response = await axiosPrivate.get('/api/admin/sales-order/export-all');

      const data = response.data.data;

      // Format the data for Excel
      const formattedData = data.map((row: any, index: number) => ({
        'No.': index + 1,
        'Order ID': row.salesOrderId,
        'Customer Name': row.fullname,
        'Phone Number': row.phone_number,
        'Amount (RM)': row.price,
        'Order Date': row.paidAt ? 
          new Intl.DateTimeFormat("en-MY", {
            timeZone: "Asia/Kuala_Lumpur",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
          }).format(new Date(row.paidAt)) : "Invalid Transaction",
        'Courier Service': row.courierService || '-',
        'Tracking Number': row.trackingNumber || '-',
      }));

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'All Orders');

      // Set column widths
      worksheet['!cols'] = [
        { wch: 5 },  // No.
        { wch: 50 }, // Order ID
        { wch: 40 }, // Customer Name
        { wch: 15 }, // Phone Number
        { wch: 15 }, // Amount
        { wch: 25 }, // Order Date
        { wch: 25 }, // Courier Service
        { wch: 25 }, // Tracking Number
      ];

      // Generate filename with timestamp
      const timestamp = new Date().toLocaleDateString('en-CA', { 
        timeZone: 'Asia/Kuala_Lumpur' 
      });
      const filename = `WebOrders_Export_${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
      
      showSuccessAlert('Export successful!');
    } catch (error) {
      console.error('Export error:', error);
      showErrorAlert('Failed to export data');
    }
  };

  return (
    <div className={classes.orderContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header
          title={
            <div>
              <p>Web Order</p>
            </div>
          }
        />

        <div className={classes.searchBarContainer}>
          <div style={{ flex: 1 }}>
            <Flex direction="column" gap="10px">
              <SearchBar
                isLoading={isLoading}
                resetPage={resetPage}
                changeTableState={changeTableState}
                search={search}
                handleSearch={handleSearch}
                setShowFilterOptions={setShowFilterOptions}
                showFilterOptions={showFilterOptions}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                checkboxesFilter={checkboxesFilter}
              />
              <DatePickerInput
                type="range"
                placeholder="Filter order date by range"
                value={dateRange}
                onChange={setDateRange}
                leftSection={
                  <IconCalendar
                    style={{ width: "16px", height: "16px" }}
                    stroke={1.5}
                  />
                }
                style={{
                  width: "369px",
                  paddingLeft: "20px",
                  paddingBottom: "10px"
                }}
                clearable
              />
            </Flex>
          </div>
            <div className={classes.scoreCardRow}>
              <ScoreCard 
                score={isLoading? "-" : pendingCount} 
                title={"Pending"}/>
              <ScoreCard 
                score={isLoading? "-" : packingCount} 
                title={`Packing`}/>
              <ScoreCard 
                score={isLoading? "-" : fulfilledCount} 
                title={`Fulfilled`}/>
              <ScoreCard 
                score={isLoading? "-" : currMthCount} 
                title={`Current Month\n(MYR)`}/>
              <ScoreCard 
                score={isLoading? "-" : prevMthCount} 
                title={`Previous Month\n(MYR)`}/>
              <div className={classes.card} >
                <Button
                  onClick={handleExportTable}
                  className={classes.exportButton}
                  >
                  <div>
                    <IconTableExport size={32} stroke={1.5} />
                    <Text fw={700} fz="md" className={classes.cardTitle} >
                      Export Table
                    </Text> 
                  </div>
                </Button>
              </div>
            </div>
        </div>
        <Tabs
          defaultValue="pending"
          color="blueviolet"
          variant="pills"
          onChange={(value) => {
            handleTabChange(value);
          }}
        >
            <Tabs.List className={classes.tabHeader}>
                <Tabs.Tab value="pending" className={classes.tabTitle}>
                    Pending
                </Tabs.Tab>
                <Tabs.Tab value="packing" className={classes.tabTitle}>
                    Packing
                </Tabs.Tab>
                <Tabs.Tab value="fulfilled" className={classes.tabTitle}>
                    Fulfilled
                </Tabs.Tab>
            </Tabs.List>
          <ScrollArea
            w={
              window.innerWidth -
              document.getElementById("nav-bar")!!.getBoundingClientRect().width
            }
          >
              <CustomTable
                colHeaders={Object.values(ColumnHeaderToFilterPair)}
                filters={Object.values(colFilters)}
                reverseSortDirection={reverseSortDirection}
                sortBy={sortBy}
                setSorting={handleSorting}
                customclasses={classes}
                Th={Th}
                rowData={rowData}
                rows={rows}
              />
          </ScrollArea>

        </Tabs>

        <div className={classes.footerContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results `}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>

        {/*isEditMode && (
          <VerifyDealer
            isOpen={isEditMode}
            onClose={() => setEditMode(false)}
            onSaveChanges={(editedData, action, remark) =>
                handleVerifyDealer(editedData, selectedRow, action, remark)
            }
            initialData={selectedRow as RowDataDealer}
          />
        )*/}
      </ScrollArea>
    </div>
  );
}

export default WebOrder;
